import React from 'react';
import BidQuickPick from './BidQuickPick';
import { createUseStyles } from 'react-jss';
import { useT } from '../../i18n';
import { robotColorName } from '../../missionLabel';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

// The round's secondary controls — *Skip* in the status lane, *Hint* and
// *Solution* in the help strip. They are the same kind of thing (everything in
// these lanes that is not a bid), so they read the same: a flat plate sitting
// in the groove of its lane, drawn with an inset ring rather than a border, so
// it never competes with the raised BID button below it. Three plate styles in
// three stacked lanes read as three unrelated toolbars.
//
// 36px in a lane that pads it out past 44 — the lane carries the touch target,
// which is also what keeps the plate off the lane's own edges.
const GROOVE_PLATE = {
  height: '36px',
  minWidth: '44px',
  padding: '0 12px',
  background: 'none',
  border: 'none',
  borderRadius: '8px',
  color: '#d99a6a',
  fontFamily: MONO,
  fontSize: '10.5px',
  fontWeight: 700,
  letterSpacing: '1.5px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  '&:disabled': { opacity: 0.4, cursor: 'default' },
  '&:not(:disabled):active': { background: 'rgba(217,79,43,0.14)' },
};

/**
 * Mobile action bar — pressed-tin plates, 44px minimum hit targets.
 * Raised plates for actions, recessed plates for values, monospace for numbers.
 *
 * What the mobile pass changed:
 *  · skip left the bar for its own status lane, which carries the round's
 *    read-only numbers on the left (clock, announced optimum, `n / m bid`)
 *    and the round's two non-bid controls on the right (show solution, skip).
 *    Six controls in one 46px row (timer, undo, reset, bid box, BID, skip)
 *    overflowed below 360px; the numbers moved up because none of them is a
 *    hit target, and the lane had the width for two more plates.
 *  · LOBBY stacks: a full-width Start, Shuffle beside it, the mode above and
 *    the solo hint below — the same hint `Sidebar` prints under its own
 *    Start Game.
 *  · REVEAL says what happened instead of returning `null`. A bar that
 *    vanishes between rounds resizes the board under the player's thumb.
 */
const useStyles = createUseStyles({
  bar: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    padding: '9px',
    paddingBottom: 'calc(9px + env(safe-area-inset-bottom, 0px))',
    background: '#2a1d13',
    borderTop: '1px solid #6b4622',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07)',
  },
  lobbyBar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '10px 10px 12px',
    paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
    background: '#2a1d13',
    borderTop: '1px solid #6b4622',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07)',
  },
  lobbyMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
  },
  meta: {
    fontFamily: MONO,
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '1.6px',
    textTransform: 'uppercase',
    color: '#a8875d',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  lobbyRow: {
    display: 'flex',
    gap: '8px',
  },
  hint: {
    textAlign: 'center',
    fontSize: '11px',
    color: '#a8875d',
  },
  // The round's numbers and the two controls that are not bidding. Its own
  // lane because the bar below is full, and a 44px target does not survive
  // being squeezed in beside the BID plate. Its plates are 36px rather than
  // the 44px floor the rest of the phone keeps — see the height budget note
  // in CLAUDE.md; on a short screen the three bidding lanes were eating the
  // board, and these two are the least-pressed controls on the screen. They
  // sit *inside* the lane with 3px of groove above and below: at 40px in a
  // 42px lane they read as the lane itself rather than as plates in it.
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    height: '42px',
    padding: '0 10px',
    background: '#1a0f0a',
    borderTop: '1px solid #4a3020',
  },
  statusLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  statusRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  clock: {
    fontFamily: MONO,
    color: ({ urgent }) => (urgent ? '#e35c36' : '#f5e6c8'),
    fontSize: '15px',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
  },
  // A fixed fact, not a countdown — an outlined chip rather than a second
  // gauge, the same call BiddingPanel makes on the desktop side.
  bestChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    height: '24px',
    padding: '0 8px',
    borderRadius: '6px',
    boxShadow: 'inset 0 0 0 1px #4a3020',
    fontFamily: MONO,
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    color: '#a8875d',
    whiteSpace: 'nowrap',
  },
  bestValue: {
    color: '#f5e6c8',
    fontSize: '13px',
    fontVariantNumeric: 'tabular-nums',
  },
  skipBtn: {
    ...GROOVE_PLATE,
    flexShrink: 0,
    boxShadow: 'inset 0 0 0 1px #6b4622',
  },
  btn: {
    minWidth: '44px',
    height: '42px',
    flexShrink: 0,
    background: '#3a2718',
    border: '1px solid #533620',
    borderRadius: '8px',
    color: '#e8d6ae',
    cursor: 'pointer',
    fontSize: '17px',
    fontFamily: 'inherit',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -2px 0 rgba(0,0,0,0.3)',
    '&:disabled': {
      opacity: 0.35,
      cursor: 'default',
      boxShadow: 'none',
    },
    '&:not(:disabled):active': {
      boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.5)',
    },
  },
  movesBox: {
    flex: 1,
    minWidth: 0,
    height: '42px',
    boxSizing: 'border-box',
    background: '#150d07',
    border: '1px solid #4a3020',
    borderRadius: '8px',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '6px',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
  },
  movesLabel: {
    fontFamily: MONO,
    color: '#9c8168',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },
  bidInput: {
    width: '52px',
    minWidth: 0,
    background: 'transparent',
    border: 'none',
    color: '#f5e6c8',
    fontFamily: MONO,
    fontSize: '17px',
    fontWeight: 700,
    textAlign: 'right',
    outline: 'none',
    padding: 0,
  },
  bidBtn: {
    height: '42px',
    flexShrink: 0,
    padding: '0 18px',
    background: '#d94f2b',
    border: '1px solid #120c07',
    borderRadius: '8px',
    color: '#fff3e6',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 700,
    fontFamily: 'inherit',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), 0 2px 0 #7d2a12',
    '&:disabled': {
      opacity: 0.35,
      cursor: 'default',
      boxShadow: 'none',
    },
    '&:not(:disabled):active': {
      transform: 'translateY(2px)',
      boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.4)',
    },
  },
  startBtn: {
    flex: 1,
    height: '52px',
    background: '#d94f2b',
    border: '1px solid #120c07',
    borderRadius: '9px',
    color: '#fff3e6',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: 700,
    fontFamily: 'inherit',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3), 0 3px 0 #7d2a12',
    '&:active': { transform: 'translateY(3px)', boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.4)' },
  },
  shuffleBtn: {
    width: '104px',
    height: '52px',
    flexShrink: 0,
    background: '#3a2718',
    border: 'none',
    borderRadius: '9px',
    color: '#e8d6ae',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '12px',
    fontFamily: 'inherit',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -3px 0 rgba(0,0,0,0.4)',
    '&:active': { boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)' },
  },
  timerBox: {
    height: '52px',
    boxSizing: 'border-box',
    flexShrink: 0,
    padding: '0 11px',
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: '2px',
    background: '#150d07',
    border: '1px solid #4a3020',
    borderRadius: '9px',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
  },
  timerValue: {
    fontFamily: MONO,
    color: ({ urgent }) => (urgent ? '#e35c36' : '#f5e6c8'),
    fontSize: '19px',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: '52px',
  },
  timerLabel: {
    fontFamily: MONO,
    color: '#9c8168',
    fontSize: '11px',
    fontWeight: 700,
  },
  swipeHint: {
    flex: 1,
    minWidth: 0,
    height: '52px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#150d07',
    borderRadius: '9px',
    boxShadow: 'inset 0 0 0 1px #4a3020, inset 0 2px 5px rgba(0,0,0,0.55)',
    fontFamily: MONO,
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: '#d9c3a2',
  },
  undoLarge: {
    width: '56px',
    height: '52px',
    flexShrink: 0,
    background: '#3a2718',
    border: 'none',
    borderRadius: '9px',
    color: '#e8d6ae',
    fontSize: '19px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -3px 0 rgba(0,0,0,0.4)',
    '&:active': { boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)' },
  },
  giveUpBtn: {
    height: '52px',
    flexShrink: 0,
    padding: '0 16px',
    background: 'none',
    border: 'none',
    borderRadius: '9px',
    boxShadow: 'inset 0 0 0 1px #d94f2b',
    color: '#e35c36',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 700,
    fontFamily: 'inherit',
    '&:active': { background: 'rgba(217,79,43,0.14)' },
  },
  statusText: {
    flex: 1,
    color: '#d9c3a2',
    fontSize: '13.5px',
    textAlign: 'center',
    lineHeight: 1.35,
  },
  statusMono: {
    fontFamily: MONO,
    color: '#9c8168',
    fontSize: '11px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },
  // Its own strip above the bar rather than more plates inside it: the bidding
  // bar is already at capacity, and a 44px minimum target does not survive
  // being squeezed in beside the bid button. The status lane below carries the
  // round's numbers and its way out (skip); this strip carries the two ways of
  // asking for help, which are a different kind of thing and are the ones a
  // player reaches for least.
  //
  // The help plates sit at 88% of the bar's width rather than running edge to
  // edge with it — about 8% narrower than the bar underneath — and they are
  // `GROOVE_PLATE`s, the same plate *Skip* is, since they are the same kind of
  // control. The lane is what carries the 44px touch target, so the plate can
  // be trimmed to 36 without spending the floor.
  solutionStrip: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    width: '88%',
    margin: '0 auto',
    // Even padding, not `6px 0 0`: the plates used to run flush to the lane's
    // bottom edge and butt against the lane under them. This is what makes the
    // 36px plate a 48px lane, so the touch target survives the trim.
    padding: '6px 0',
  },
  solutionBtn: {
    ...GROOVE_PLATE,
    // Shares the strip rather than hugging its label: two plates, one row.
    flex: 1,
    boxShadow: ({ playing }) => `inset 0 0 0 1px ${playing ? '#e35c36' : '#6b4622'}`,
    color: ({ playing }) => (playing ? '#e35c36' : '#d99a6a'),
  },
  // The hint readout rides in the same strip as the buttons, on its own row —
  // the strip is already the only place above the bar with room to spare.
  // The readout is what the Hint plate answered with, so it lines up with the
  // plate rather than with the bar.
  hintReadout: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '88%',
    margin: '0 auto',
    padding: '6px 0 0',
    fontFamily: MONO,
    fontSize: '11px',
    letterSpacing: '1px',
    color: '#f0dcc0',
  },
  hintSwatch: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
    background: ({ hintColor }) => hintColor ?? 'transparent',
    boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.45)',
  },
  hintStep: {
    marginLeft: 'auto',
    fontSize: '9.5px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: '#9c8168',
  },
});

function MobileControls({
  phase, sandboxMoves, isSelfDemo, demoMovesRemaining,
  demoPlayerId, players, mode, bids, biddingTimer, demoTimer, optimalMoves,
  onUndo, onReset, onBid, onGiveUp, onReadyToSkip, onUndoReadyToSkip, onStartGame, onReshuffleBoard,
  isHost, iAmReady, everyoneReady,
  hasBid, rebidOpen, canBid, standingBid, bidValue, bidInputProps, bidInputRef,
  onPickBid, onMoreBids, quickPick = true,
  onShowSolution, onStopSolution, solutionProgress, onHint, hint,
}) {
  const showBiddingTimer = phase === 'BIDDING' && biddingTimer !== null && biddingTimer !== undefined;
  const showDemoTimer = phase === 'DEMO' && demoTimer !== null && demoTimer !== undefined;
  const urgent = showDemoTimer
    ? demoTimer <= 10 // demo turns run 15-60s, so 15 would flag the whole turn
    : showBiddingTimer && biddingTimer <= 15;
  const playing = solutionProgress != null;
  const classes = useStyles({ urgent, playing, hintColor: hint?.color });
  const t = useT();

  // Its own strip above the bar rather than more plates inside it: the bidding
  // bar is already at capacity, and a 44px minimum target does not survive
  // being squeezed in beside the bid button.
  //
  // The plates say 'Hint' and 'Solution'; the whole instruction stays on the
  // accessible name, so a screen reader hears the sentence the strip has no
  // room to print.
  const helpStrip = (onShowSolution || onHint) ? (
    <>
      <div className={classes.solutionStrip}>
        {onHint && (
          <button className={classes.solutionBtn} onClick={onHint}>
            {hint ? t('bidding.hintAgain') : t('bidding.hint')}
          </button>
        )}
        {onShowSolution && (
          <button
            className={classes.solutionBtn}
            aria-label={playing
              ? t('bidding.playingSolution', {
                step: solutionProgress.step, total: solutionProgress.total,
              })
              : t('bidding.showSolution')}
            onClick={playing ? onStopSolution : onShowSolution}
          >
            {playing
              ? t('bidding.solutionStop', {
                step: solutionProgress.step, total: solutionProgress.total,
              })
              : t('bidding.solutionShort')}
          </button>
        )}
      </div>
      {hint && (
        <div className={classes.hintReadout}>
          <span className={classes.hintSwatch} />
          <span>{robotColorName(hint.color, t)}</span>
          <span className={classes.hintStep}>
            {t('bidding.hintGiven', { step: hint.step, total: hint.total })}
          </span>
        </div>
      )}
    </>
  ) : null;

  if (phase === 'LOBBY') {
    // No seat count anywhere near this: the server turns nobody away, it hands
    // a fifth player a colour already in use, so `n / 4` would advertise a cap
    // that does not exist. Same rule as ScoreBoard's open row.
    return (
      <div className={classes.lobbyBar}>
        <div className={classes.lobbyMeta}>
          <span className={classes.meta}>{t('game.waitingForPlayers')}</span>
          {mode && <span className={classes.meta}>{t(`modes.${mode}`)}</span>}
        </div>
        {isHost ? (
          <>
            <div className={classes.lobbyRow}>
              <button className={classes.shuffleBtn} onClick={onReshuffleBoard}>
                {t('game.shuffle')}
              </button>
              <button className={classes.startBtn} onClick={onStartGame}>
                {t('game.startGame')}
              </button>
            </div>
            <div className={classes.hint}>{t('game.startHint')}</div>
          </>
        ) : (
          <div className={classes.statusText}>{t('game.waitingForHost')}</div>
        )}
      </div>
    );
  }

  // Not null: a bar that disappears between rounds resizes the board under the
  // player's thumb. Keep the height, say what happened.
  if (phase === 'REVEAL') {
    return (
      <div className={classes.bar}>
        <div className={classes.statusText}>{t('game.missionRevealed')}</div>
      </div>
    );
  }

  if (phase === 'BIDDING') {
    const bidCount = bids?.length ?? 0;
    const low = bidCount ? Math.min(...bids.map(b => b.count)) : null;
    return (
      <>
        {/* Numbers left, controls right. The bid count is the only thing here
          * that may be truncated — the clock and the optimum are fixed width,
          * and the skip plate is a hit target. */}
        <div className={classes.statusRow}>
          <div className={classes.statusLeft}>
            {showBiddingTimer && <span className={classes.clock}>{biddingTimer}s</span>}
            {optimalMoves != null && (
              <span className={classes.bestChip}>
                {t('bidding.best')}
                <span className={classes.bestValue}>{optimalMoves}</span>
              </span>
            )}
            <span className={classes.meta}>
              {t('bidding.placedCount', { count: bidCount, total: players?.length ?? 0 })}
              {low !== null && ` · ${t('bidding.low', { count: low })}`}
            </span>
          </div>
          <div className={classes.statusRight}>
            {iAmReady && !everyoneReady ? (
              <button className={classes.skipBtn} onClick={onUndoReadyToSkip}>
                {t('bidding.cancel')}
              </button>
            ) : (
              <button
                className={classes.skipBtn}
                disabled={iAmReady || everyoneReady}
                onClick={onReadyToSkip}
              >
                {everyoneReady ? t('bidding.readyShort') : t('bidding.skipShort')}
              </button>
            )}
          </div>
        </div>
        {helpStrip}
        {quickPick && rebidOpen && (
          <BidQuickPick
            value={bidValue}
            onPick={onPickBid}
            onMore={onMoreBids}
            maxBid={standingBid}
          />
        )}
        <div className={classes.bar}>
          {hasBid && !rebidOpen ? (
            <div className={classes.movesBox} style={{ justifyContent: 'center' }}>
              <span className={classes.movesLabel}>{t('bidding.bidPlacedLong')}</span>
            </div>
          ) : (
            <>
              <button className={classes.btn} onClick={onUndo} disabled={!sandboxMoves} aria-label={t('bidding.undo')}>
                ↩
              </button>
              <button className={classes.btn} onClick={onReset} aria-label={t('bidding.reset')}>
                ↻
              </button>
              <div className={classes.movesBox}>
                <span className={classes.movesLabel}>{t('bidding.bid')}</span>
                <input
                  className={classes.bidInput}
                  ref={bidInputRef}
                  aria-label={t('bidding.bid')}
                  {...bidInputProps}
                />
              </div>
              <button
                className={classes.bidBtn}
                disabled={!canBid}
                onClick={onBid}
              >
                {hasBid ? t('bidding.rebid') : t('bidding.bid')}
              </button>
            </>
          )}
        </div>
      </>
    );
  }

  if (phase === 'DEMO') {
    const demoPlayer = players?.find(p => p.id === demoPlayerId);
    const movesLabel = demoMovesRemaining === 0
      ? t('demo.outOfMovesShort')
      : t('demo.movesLeft', { count: demoMovesRemaining });
    if (isSelfDemo) {
      return (
        <div className={classes.bar}>
          <button className={classes.undoLarge} onClick={onUndo} aria-label={t('demo.undo')}>
            ↩
          </button>
          {showDemoTimer && (
            <div className={classes.timerBox}>
              <span className={classes.timerValue}>{demoTimer}</span>
              <span className={classes.timerLabel}>s</span>
            </div>
          )}
          <div className={classes.swipeHint}>{movesLabel}</div>
          <button className={classes.giveUpBtn} onClick={onGiveUp}>
            {t('demo.giveUp')}
          </button>
        </div>
      );
    }
    return (
      <div className={classes.bar}>
        <div className={classes.statusText}>
          {t('demo.statusOther', { name: demoPlayer?.username ?? t('log.someone') })}
          <div className={classes.statusMono}>
            {showDemoTimer ? `${demoTimer}s · ${movesLabel}` : movesLabel}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.bar}>
      <div className={classes.statusText}>
        {phase === 'SOLVED' ? t('game.roundSolved')
          : phase === 'SKIP' ? t('game.missionFailed')
            : phase === 'FAILED' ? t('game.demoFailed') : t('game.gameOver')}
      </div>
    </div>
  );
}

export default MobileControls;
